import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Collapse, message } from "antd";
import {
  ArrowSquareOut,
  CheckCircle,
  DownloadSimple,
  FileArrowDown,
  Sparkle,
  WarningCircle,
  XCircle,
} from "@phosphor-icons/react";
import {
  listBriefFeedback,
  listMatterAnalysisJobs,
  saveBriefAsDocument,
} from "../../service/analysis";
import BriefFeedbackBar from "./BriefFeedbackBar";
import BriefChatPanel from "./BriefChatPanel";
import {
  AnalysisErrorBanner,
  AnalysisHelperText,
  AnalysisStatusPill,
  AnalysisStatusPillDot,
  BriefingSkeletonBlock,
  BriefingSkeletonGroup,
  CombinedRatingCard,
  CombinedRatingNote,
  CombinedRatingValue,
  ConditionAnalysisBody,
  ConditionAnalysisCollapse,
  ConditionFailedBanner,
  ConditionMetaChip,
  ConditionMetaChipRow,
  ConditionPanelHeader,
  ConditionPanelTitle,
  ConditionStatusIconWrap,
  ReportEmptyBody,
  ReportFooter,
  ReportHeaderMeta,
  ReportHeaderRow,
  ReportHeaderSubtitle,
  ReportHeaderTitle,
  StrategicOverviewBody,
  StrategicOverviewCard,
} from "./AIPaneStyled";
import BriefingMarkdown from "./BriefingMarkdown";
import {
  clearReportForJob,
  fetchAnalysisJobReport,
  selectIsAnalysisRunningForDocument,
  selectReportForJob,
  triggerAnalysisJob,
} from "../../slice/AISlice";
import { handleCitationClick } from "../../utils/citationJump";

// Re-export for tests that historically imported the local handler
// directly. The implementation now lives in ``citationJump.js`` so all
// three Brief / Briefing / Context-Analysis surfaces share one source
// of truth (Bugbot Low #PR-106). Existing test files keep working
// without churn; new tests should import ``handleCitationClick`` from
// ``citationJump.js`` directly.
export const _handleCitationClick = handleCitationClick;

/**
 * Phase 4 Analysis report view.
 *
 * Renders the full attorney-analysis report once a job reaches a terminal
 * status (`completed` / `completed_with_errors`). Three visual blocks:
 *
 *   1. Header — completed-at timestamp + total elapsed time + status pill.
 *   2. Strategic Overview card — distinct brand-blue accent block holding
 *      the synthesised narrative from `discovery_data.strategic_narrative`.
 *      Markdown body is rendered via `BriefingMarkdown` so citation pills
 *      + GFM stay consistent with the inbox briefing.
 *   3. Per-condition collapsible cards — antd `Collapse`, one panel per
 *      `condition_analyses[]` row. Each header carries metadata chips
 *      (service connection / diagnostic code / current rating /
 *      effective date / category / secondary-to). The body renders
 *      `analysis_text` markdown plus a failure banner when applicable.
 *   4. Footer — Export action that downloads the report as a self-
 *      contained markdown file (no PDF dependency to keep this Phase
 *      shippable; PDF rendering can be a Phase 4.1 follow-up).
 *
 * Lifecycle: this component dispatches `fetchAnalysisJobReport`
 * whenever the cached slot for `jobId` is `idle` — covering mount,
 * jobId changes (a re-run produces a fresh job id), and external cache
 * evictions (a tab close elsewhere in the matter dispatches
 * `clearDocumentAIState`, which may drop this job's report). On
 * unmount the report cache for *that specific jobId* is preserved so
 * the user can flip back to the Analysis tab without re-fetching.
 * Memory cap is implicit: at most one cached report per (job, doc) —
 * the user can't accumulate reports faster than they can run analyses.
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_LABEL = {
  completed: "Completed",
  completed_with_errors: "Completed with errors",
  failed: "Failed",
  cancelled: "Cancelled",
};

const formatDuration = (createdAt, completedAt) => {
  if (!createdAt || !completedAt) return null;
  const startMs = Date.parse(createdAt);
  const endMs = Date.parse(completedAt);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) {
    return null;
  }
  const totalSec = Math.round((endMs - startMs) / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min === 0) return `${sec}s`;
  return `${min}m ${String(sec).padStart(2, "0")}s`;
};

const formatTimestamp = (iso) => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  // Local-time formatter — the user is reading their own report, so
  // their timezone is the right anchor.
  try {
    return new Date(ms).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_err) {
    return new Date(ms).toISOString();
  }
};

/**
 * Pull the strategic narrative out of `discovery_data`. Backend stores
 * this in two shapes (legacy + post-3.4b):
 *   1. `discovery_data` is a list  → no narrative yet
 *   2. `discovery_data` is `{ conditions, strategic_narrative }`
 *      (post-3.4b) → narrative lives here
 * Returning `null` keeps the Strategic Overview card from rendering at
 * all when there's nothing to show, which is the right call for failed
 * jobs (they don't run the synthesis pass).
 */
// Regex matching a numbered top-level section header — `1. Current
// Position`, `2. Likelihood-Ranked Opportunities`, etc. Anchored to a
// preceding non-whitespace character so we don't add a break before
// the FIRST `1.` at the start of the narrative. Mirrors the BE
// `_SECTION_HEADER_RE` in `doc_pipeline/briefing/report_markdown.py`.
const SECTION_HEADER_RE = /(\S)\s+(\d{1,2}\.\s+[A-Z])/g;

// Inline bullet marker: any whitespace + `*` + whitespace, followed by
// a capital letter (start of a list item) or an opening parenthesis.
// Mirrors the BE `_INLINE_BULLET_RE`.
const INLINE_BULLET_RE = /\s+\*\s+(?=[A-Z(])/g;

// Tier-tagged bullet line: `* <name> — HIGH:` (or MEDIUM/LOW). Em-dash
// (—) and hyphen-minus (-) both accepted defensively. Mirrors the BE
// `_TIER_BULLET_RE`. Used per-line, so no global flag.
const TIER_BULLET_RE = /^\*\s+(.+?)\s+[\u2014-]\s+(HIGH|MEDIUM|LOW)\s*:/;

const titleCaseTier = (tier) =>
  tier.charAt(0) + tier.slice(1).toLowerCase();

// Case-insensitive match for `**<tier> likelihood**` headings. The
// LLM prompt instructs it to emit `**High Likelihood**` etc., but
// models sometimes pick upper- or lower-case. We treat all three as
// equivalent for the idempotence check so we don't stack two
// headings on top of each other. Mirrors the BE `_is_same_tier_heading`
// in `doc_pipeline/briefing/report_markdown.py`.
const isSameTierHeading = (line, tier) => {
  if (!line) return false;
  const re = new RegExp(
    `^\\*\\*\\s*${tier}\\s+likelihood\\s*\\*\\*\\s*$`,
    "i"
  );
  return re.test(line.trim());
};

const groupBulletsByTier = (text) => {
  const lines = text.split("\n");
  const out = [];
  let currentTier = null;
  const lastNonBlank = () => {
    for (let i = out.length - 1; i >= 0; i--) {
      if (out[i].trim()) return out[i];
    }
    return null;
  };
  for (const line of lines) {
    const m = line.match(TIER_BULLET_RE);
    if (m) {
      const tier = m[2];
      if (tier !== currentTier) {
        const expected = `**${titleCaseTier(tier)} likelihood**`;
        // Idempotence: skip the heading if a same-tier heading (any
        // case) is already directly above this bullet.
        const prev = lastNonBlank();
        if (!isSameTierHeading(prev || "", tier)) {
          if (out.length > 0 && out[out.length - 1].trim() !== "") {
            out.push("");
          }
          out.push(expected);
          out.push("");
        }
        currentTier = tier;
      }
      out.push(line);
    } else {
      if (currentTier !== null && line.trim() !== "") {
        if (out.length > 0 && out[out.length - 1].trim() !== "") {
          out.push("");
        }
        currentTier = null;
      }
      out.push(line);
    }
  }
  return out.join("\n");
};

/**
 * Coerce the LLM-emitted strategic narrative into well-formed markdown.
 *
 * Mirrors the BE ``_normalize_narrative`` in
 * ``doc_pipeline/briefing/report_markdown.py``. Three transforms applied
 * in order:
 *   1. Section breaks — newline before run-on numbered section headers.
 *   2. Inline-bullet split — ` * ` → `\n* ` so list items render.
 *   3. Tier grouping — bold subheadings at HIGH/MEDIUM/LOW transitions.
 *
 * Idempotent: safe to apply twice; produces the same output the second
 * time because each transform only fires on the run-on shape.
 *
 * Used both by `composeReportMarkdown` (export-as-markdown + the saved
 * Brief PDF must look the same) and by the on-screen Strategic Overview
 * render so the panel view matches the saved doc.
 */
export const normalizeNarrative = (narrative) => {
  if (typeof narrative !== "string") return narrative;
  const trimmed = narrative.trim();
  if (!trimmed) return trimmed;
  let text = trimmed.replace(SECTION_HEADER_RE, "$1\n\n$2");
  text = text.replace(INLINE_BULLET_RE, "\n* ");
  text = groupBulletsByTier(text);
  return text;
};

const extractStrategicNarrative = (report) => {
  const dd = report?.discovery_data;
  if (!dd || typeof dd !== "object" || Array.isArray(dd)) return null;
  const narrative = dd.strategic_narrative;
  if (typeof narrative !== "string" || !narrative.trim()) return null;
  return narrative;
};

const renderRatingValue = (rating) => {
  if (rating == null) return null;
  if (typeof rating === "number") return `${rating}%`;
  if (typeof rating === "string" && rating.trim()) {
    const trimmed = rating.trim();
    return /[%]$/.test(trimmed) ? trimmed : `${trimmed}%`;
  }
  return null;
};

/**
 * Resolve the SC chip with the same precedence the BE serializer
 * applies. Prefer the BE-derived ``chip_service_connection`` field
 * (added to ``ConditionAnalysisDetailSerializer`` so chip rendering has
 * one canonical source of truth across the FE chip strip and the
 * saved-PDF / .md export). Fall back to the legacy two-source
 * resolution for older API responses or test fixtures that pre-date
 * the field.
 *
 * Why centralise this on the BE: PR #333 fixed the precedence in
 * ``report_markdown._build_condition_chips`` (graph status wins when
 * it carries an unambiguous SC/Denial verdict, codesheet otherwise)
 * but left this FE function with the inverted "codesheet-first" rule.
 * The result was Allergic Rhinitis on the user's 2026-04-30 matter
 * showing "Not service-connected" in the right-rail chip while the
 * body of the analysis said "Service Connected: Yes — Current Rating:
 * 0% — Effective Date: 2025-02-13", because the on-file codesheet
 * pre-dated the 2025-06-16 rating decision that granted SC. Both
 * surfaces now read the same value, and any future tweak to the
 * precedence lives in one place
 * (``ConditionAnalysisDetailSerializer.get_chip_service_connection``).
 */
const resolveScChip = (condition) => {
  const derived = condition?.chip_service_connection;
  if (derived === "service_connected") {
    return {
      key: "sc",
      tone: "serviceConnected",
      label: "Service-connected",
    };
  }
  if (derived === "not_service_connected") {
    return {
      key: "sc",
      tone: "notServiceConnected",
      label: "Not service-connected",
    };
  }
  if (typeof derived === "string" && derived.trim()) {
    return { key: "cs", tone: "neutral", label: derived.trim() };
  }
  if (derived === null) {
    return null;
  }
  // Legacy fallback: BE has not been deployed yet OR a test fixture
  // doesn't include the field. Mirrors the BE precedence rule so the
  // visible output stays consistent during the rollout window.
  const meta = condition?.condition_metadata || {};
  const codesheetSc = meta.codesheet_service_connected;
  const graphStatus = condition?.condition_status;
  const graphNorm =
    typeof graphStatus === "string" ? graphStatus.trim().toLowerCase() : "";
  if (graphNorm === "service connected") {
    return {
      key: "sc",
      tone: "serviceConnected",
      label: "Service-connected",
    };
  }
  if (graphNorm === "denied" || graphNorm === "denial") {
    return {
      key: "sc",
      tone: "notServiceConnected",
      label: "Not service-connected",
    };
  }
  if (codesheetSc === true) {
    return {
      key: "sc",
      tone: "serviceConnected",
      label: "Service-connected",
    };
  }
  if (codesheetSc === false) {
    return {
      key: "sc",
      tone: "notServiceConnected",
      label: "Not service-connected",
    };
  }
  if (typeof graphStatus === "string" && graphStatus.trim()) {
    return { key: "cs", tone: "neutral", label: graphStatus.trim() };
  }
  return null;
};

/**
 * The wordings the veteran's own filings used, when they differ from
 * VA's characterization. VA states these beside the condition it
 * adjudicated — "LEFT HIP PAIN (claimed as BILATERAL HIP PAIN)" — so a
 * reader can tell the claim said something else. The BE serves a
 * filtered `claimed_as` list on both the status poll and the report;
 * `condition_metadata` is the fallback for reports rendered from cache
 * that predate that field.
 */
const readClaimedWordings = (condition) => {
  const raw =
    condition?.claimed_as ?? condition?.condition_metadata?.claimed_as ?? [];
  const values = Array.isArray(raw) ? raw : [raw];
  return values
    .map((value) => String(value ?? "").trim())
    .filter((value) => value.length > 0);
};

/**
 * Build the chip strip for a condition. Each chip is a `{ key, tone,
 * label }` so the renderer can stay dumb and the chip ordering reflects
 * importance: SC state → claimed-as → diagnostic code → rating →
 * effective date → category → secondary-to. The SC chip itself is
 * resolved by `resolveScChip` so the BE-derived precedence rule lives
 * in one place.
 */
const buildConditionChips = (condition) => {
  const chips = [];
  const meta = condition?.condition_metadata || {};
  const scChip = resolveScChip(condition);
  if (scChip) {
    chips.push(scChip);
  }

  const claimedWordings = readClaimedWordings(condition);
  if (claimedWordings.length > 0) {
    chips.push({
      key: "claimed-as",
      tone: "neutral",
      label: `claimed as ${claimedWordings.join(", ")}`,
    });
  }

  if (meta.diagnostic_code) {
    chips.push({
      key: "dc",
      tone: "neutral",
      label: `DC ${String(meta.diagnostic_code).trim()}`,
    });
  }

  const rating = renderRatingValue(meta.codesheet_rating);
  if (rating) {
    chips.push({
      key: "rate",
      tone: "rating",
      label: rating,
    });
  }

  if (meta.codesheet_effective_date) {
    chips.push({
      key: "eff",
      tone: "neutral",
      label: `Eff. ${String(meta.codesheet_effective_date).trim()}`,
    });
  }

  if (meta.category) {
    chips.push({
      key: "cat",
      tone: "neutral",
      label: String(meta.category).trim(),
    });
  }

  if (meta.secondary_to) {
    chips.push({
      key: "sec",
      tone: "warning",
      label: `Secondary to ${String(meta.secondary_to).trim()}`,
    });
  }

  return chips;
};

const ConditionStatusIcon = ({ status: rowStatus }) => {
  if (rowStatus === "failed") {
    return (
      <ConditionStatusIconWrap $status="failed" aria-label="Failed">
        <XCircle size={16} weight="fill" />
      </ConditionStatusIconWrap>
    );
  }
  return (
    <ConditionStatusIconWrap $status="completed" aria-label="Completed">
      <CheckCircle size={16} weight="fill" />
    </ConditionStatusIconWrap>
  );
};
ConditionStatusIcon.propTypes = { status: PropTypes.string };
ConditionStatusIcon.defaultProps = { status: "completed" };

// ---------------------------------------------------------------------------
// Export — Markdown
// ---------------------------------------------------------------------------

/**
 * Compose a self-contained markdown export of the report. We don't
 * round-trip through the backend `_compile_full_report` Python because
 * (a) the FE already has all the data and (b) keeping the export source
 * client-side means the user gets exactly what they're reading on
 * screen, including chip metadata. Layout mirrors the on-screen view
 * for parity:
 *
 *   # Attorney Analysis Report
 *   _Generated <timestamp>_
 *
 *   ## Strategic Overview
 *   ...
 *
 *   ## Per-Condition Analysis
 *   ### 1. PTSD  (chips...)
 *   ...
 */
export const composeReportMarkdown = (report) => {
  if (!report) return "";
  const lines = [];
  lines.push("# Attorney Analysis Report");
  if (report.completed_at) {
    const ts = formatTimestamp(report.completed_at);
    if (ts) lines.push(`_Completed ${ts}_`);
  }
  lines.push("");

  const combined = report.combined_rating;
  if (combined?.combined_pct != null) {
    lines.push(`**Combined Rating (codesheet): ${combined.combined_pct}%**`);
    if (combined.note) lines.push(`_${combined.note}_`);
    lines.push("");
  }

  const narrative = extractStrategicNarrative(report);
  if (narrative) {
    lines.push("## Strategic Overview");
    lines.push("");
    lines.push(normalizeNarrative(narrative));
    lines.push("");
  }

  const conditions = Array.isArray(report.condition_analyses)
    ? [...report.condition_analyses].sort((a, b) => {
        const aSeq =
          typeof a?.sequence_number === "number" ? a.sequence_number : 0;
        const bSeq =
          typeof b?.sequence_number === "number" ? b.sequence_number : 0;
        return aSeq - bSeq;
      })
    : [];

  if (conditions.length > 0) {
    lines.push("## Per-Condition Analysis");
    lines.push("");
    conditions.forEach((cond, idx) => {
      const num = typeof cond.sequence_number === "number"
        ? cond.sequence_number
        : idx + 1;
      lines.push(`### ${num}. ${cond.condition_name || "Unnamed condition"}`);
      const chips = buildConditionChips(cond);
      if (chips.length > 0) {
        // Chip labels read fine inline as parenthetical metadata in
        // the exported file — no need to wrap each in backticks.
        lines.push(`_${chips.map((c) => c.label).join(" · ")}_`);
      }
      lines.push("");
      if (cond.status === "failed") {
        lines.push(`> **Failed:** ${cond.error_message || "no error message"}`);
        lines.push("");
      } else if (cond.analysis_text) {
        lines.push(cond.analysis_text.trim());
        lines.push("");
      } else {
        lines.push("_No analysis text available._");
        lines.push("");
      }
    });
  }

  return lines.join("\n");
};

// Filenames must be ASCII-only. AWS S3 metadata (`original-key` written
// by the backend PDF optimizer) enforces strict-ASCII on values, so
// any em-dash (U+2014) or en-dash (U+2013) that escapes into a filename
// blows up downstream — this is the proximate cause of Sentry
// DMS-BACKEND-3H, where the backend's saved-Brief PDF filename
// (`<client_name> — The Brief — <date>.pdf`) crashed every linearization
// pass. The backend is shipping the same swap on its side
// (`doc_pipeline/briefing/save_brief.py`); this FE-side preprocessor is
// the symmetric guarantee for the markdown-export filename and any
// future filename template that folds in user-typed content (matter
// name, client name, etc.). Em/en-dashes get rewritten to " - " BEFORE
// the non-ASCII strip so the result is readable ("Richard Gilliland - The
// Brief") instead of underscore-collapsed garbage ("Richard_Gilliland").
// Em-dashes inside the rendered markdown BODY are deliberately
// preserved — body content goes through `composeReportMarkdown`, which
// never touches `sanitizeFilename`.
export const sanitizeFilename = (s) =>
  String(s || "")
    .replace(/[\u2014\u2013]/g, " - ")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);

export const buildExportFilename = (report) => {
  // Format: analysis-report__job-<id>__<yyyy-mm-dd>.md
  const datePart = (() => {
    if (!report?.completed_at) return "";
    const ms = Date.parse(report.completed_at);
    if (Number.isNaN(ms)) return "";
    const d = new Date(ms);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `__${y}-${m}-${day}`;
  })();
  const jobPart = report?.id != null ? `__job-${report.id}` : "";
  return sanitizeFilename(`analysis-report${jobPart}${datePart}`) + ".md";
};

export const buildBriefReaderPath = ({ matterId, jobId } = {}) => {
  if (matterId == null || jobId == null) return null;
  return `/matter/${encodeURIComponent(String(matterId))}/brief/${encodeURIComponent(
    String(jobId)
  )}`;
};

/**
 * Trigger a browser download of `markdown` as a file. Pure DOM API; we
 * don't pull in `file-saver` for one operation. Schedules the
 * `URL.revokeObjectURL` after the click handler unwinds so Chrome and
 * Firefox both finish reading the blob — calling revoke synchronously
 * sometimes cancels the download in Firefox.
 */
export const triggerMarkdownDownload = (markdown, filename) => {
  if (!markdown || typeof window === "undefined") return false;
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
    return false;
  }
  const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Defer the revoke — Firefox aborts the download if we revoke
  // synchronously after click().
  setTimeout(() => {
    try {
      URL.revokeObjectURL(url);
    } catch (_err) {
      // ignore — best-effort cleanup
    }
  }, 1000);
  return true;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AnalysisReport = ({
  jobId = null,
  variant = "panel",
  onCitationClick = undefined,
  showReaderAction = true,
  fallbackMatterId = null,
}) => {
  const dispatch = useDispatch();
  const reportState = useSelector((state) => selectReportForJob(state, jobId));
  const isStandalone = variant === "standalone";

  // Fetch whenever the cached slot is `idle` — on mount, on jobId
  // change, AND whenever something external drops the cached report
  // (e.g. `clearDocumentAIState` fired by a tab close elsewhere in the
  // matter). This component stays mounted across document switches
  // (AIPane keeps its tabs alive), so an effect keyed only on jobId
  // could never recover from an external cache eviction: the status
  // reverted to "idle", nothing re-dispatched, and the pane sat on the
  // skeleton until the user pressed Retry. Gating on `idle` cannot
  // loop (the pending reducer immediately flips the slot to
  // "loading"), and `failed` still never auto-refetches — the explicit
  // Retry button owns that path.
  useEffect(() => {
    if (jobId == null) return;
    if (reportState.status === "idle") {
      dispatch(fetchAnalysisJobReport(jobId));
    }
  }, [dispatch, jobId, reportState.status]);

  const handleRetry = () => {
    if (jobId == null) return;
    dispatch(fetchAnalysisJobReport(jobId));
  };

  const handleClearReport = () => {
    if (jobId == null) return;
    dispatch(clearReportForJob(jobId));
  };

  const report = reportState.data;
  const reportDocumentId = report?.document ?? null;
  const matterForRerun = report?.matter ?? fallbackMatterId ?? null;
  const readerPath = buildBriefReaderPath({
    matterId: report?.matter,
    jobId,
  });
  // Re-run wiring. The source document for this report drives the
  // trigger payload; we read it off the loaded report so we don't have
  // to thread an extra prop through AnalysisTab. `isJobRunning` covers
  // the rare race where the report is rendered while a sibling tab has
  // already kicked off a fresh job — in that case the button stays
  // disabled rather than firing a 409. `isReRunning` covers the local
  // dispatch window: between the click and the new "running" job
  // landing in Redux, the button shows a spinner. Once the new job is
  // in state the parent AnalysisTab swaps this view out for the
  // progress view, so we never need to re-enable from `isReRunning`
  // ourselves except on dispatch failure.
  const [fallbackRerunDocumentId, setFallbackRerunDocumentId] = useState(null);
  const [isResolvingRerunContext, setIsResolvingRerunContext] = useState(false);
  const [isMatterJobRunning, setIsMatterJobRunning] = useState(false);
  const [rerunLookupError, setRerunLookupError] = useState(null);

  useEffect(() => {
    setFallbackRerunDocumentId(null);
    setIsMatterJobRunning(false);
    setRerunLookupError(null);

    const shouldLookupRerunContext =
      report &&
      matterForRerun != null &&
      jobId != null &&
      (isStandalone || reportDocumentId == null);

    if (!shouldLookupRerunContext) {
      setIsResolvingRerunContext(false);
      return undefined;
    }

    let cancelled = false;
    let refreshTimer = null;
    const refreshRerunContext = ({ showLoading = false } = {}) => {
      if (showLoading) setIsResolvingRerunContext(true);
      listMatterAnalysisJobs(matterForRerun, { limit: 50 })
        .then((response) => {
          if (cancelled) return;
          const jobs = Array.isArray(response?.data) ? response.data : [];
          const matchingJob = jobs.find(
            (job) => String(job?.id) === String(jobId)
          );
          const recoveredDocumentId = matchingJob?.document ?? null;
          const hasRunningJob = jobs.some((job) => job?.status === "running");
          setFallbackRerunDocumentId(recoveredDocumentId);
          setIsMatterJobRunning(hasRunningJob);
          setRerunLookupError(null);
          if (reportDocumentId == null && recoveredDocumentId == null) {
            setRerunLookupError(
              "This Brief is missing its source document, so it cannot safely start a new run from here."
            );
          }
          if (hasRunningJob) {
            refreshTimer = setTimeout(refreshRerunContext, 5000);
          }
        })
        .catch(() => {
          if (cancelled) return;
          setRerunLookupError(
            "The matter's Brief jobs could not be checked, so Run again is unavailable here. Open the matter and start the Brief from the AI panel."
          );
          refreshTimer = setTimeout(refreshRerunContext, 5000);
        })
        .finally(() => {
          if (!cancelled && showLoading) {
            setIsResolvingRerunContext(false);
          }
        });
    };
    refreshRerunContext({ showLoading: true });

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [report, reportDocumentId, matterForRerun, jobId, isStandalone]);

  const reRunDocumentId = reportDocumentId ?? fallbackRerunDocumentId ?? null;
  const isJobRunning = useSelector((state) =>
    selectIsAnalysisRunningForDocument(state, reRunDocumentId)
  );
  const [isReRunning, setIsReRunning] = useState(false);
  const rerunDisabledReason = (() => {
    if (isResolvingRerunContext) {
      return "Looking up the source document before starting a new Brief.";
    }
    if (rerunLookupError) return rerunLookupError;
    if (reRunDocumentId == null) {
      return "This Brief is missing its source document, so Run again is unavailable here.";
    }
    if (isJobRunning || isMatterJobRunning) {
      return "A Brief is already running for this matter.";
    }
    return null;
  })();

  const handleRerun = async () => {
    if (rerunDisabledReason || isReRunning) return;
    setIsReRunning(true);
    try {
      const result = await dispatch(
        triggerAnalysisJob({
          documentId: reRunDocumentId,
          claimedOnly: true,
          // The matter-first AI pane renders from the matter cache, so
          // the optimistic synthetic job must land there or the pane
          // wouldn't notice the run it just started.
          matterId: matterForRerun,
        })
      );
      // Drop the old report only after the new job actually started.
      // Clearing first (the old order) now matters: the idle-refetch
      // effect above would immediately re-fetch the just-cleared
      // report while the trigger request was still in flight. And if
      // the trigger fails, keeping the old report on screen is the
      // right outcome anyway.
      if (result?.type === triggerAnalysisJob.fulfilled.type) {
        dispatch(clearReportForJob(jobId));
      }
    } finally {
      setIsReRunning(false);
    }
  };

  // Save-as-Document wiring (BE PR #327). Promotes the report into a
  // real DMS Document under an auto-created "AI Briefs" folder. The
  // matter id comes off the report payload so we don't need a prop;
  // the BE returns a stable document id across re-saves (re-saves bump
  // the SharePoint version) so the toast's "Open" link is durable
  // across "Run again" cycles.
  const navigate = useNavigate();
  const location = useLocation();
  const saveMatterId = report?.matter ?? null;
  const [isSaving, setIsSaving] = useState(false);

  const handleOpenReader = () => {
    if (!readerPath) return;
    if (typeof window === "undefined" || typeof window.open !== "function") {
      return;
    }
    window.open(readerPath, "_blank", "noopener,noreferrer");
  };

  const openSavedDocument = (savedDoc) => {
    if (!savedDoc?.id || !saveMatterId) return;
    // Use the BE-supplied `relative_path` and prefix with `/matter` —
    // this is the canonical pattern the rest of the matter UI uses
    // (see TabView.handleDocumentClick which does the same thing).
    // The hand-rolled `/matter/<id>/doc/<docId>` shape from the first
    // hotfix didn't match the route table, so React Router fell
    // through to the matter shell with no doc selected and the AI
    // panel reset to its empty state. `relative_path` already starts
    // with `/<matter_id>/documents/<doc_id>` so the prepended
    // `/matter` produces the right route.
    const relativePath = savedDoc.relative_path;
    if (typeof relativePath === "string" && relativePath.length > 0) {
      navigate(`/matter${relativePath}${location.search}`);
      return;
    }
    // Fallback when the BE response is missing relative_path: hand-build
    // the URL using the documented `/matter/:id/documents/:docId` shape.
    navigate(
      `/matter/${saveMatterId}/documents/${savedDoc.id}${location.search}`
    );
  };

  const handleSaveAsDocument = async () => {
    if (saveMatterId == null || jobId == null || isSaving) return;
    setIsSaving(true);
    try {
      const response = await saveBriefAsDocument(saveMatterId, { jobId });
      const { document: savedDoc, version } = response?.data || {};
      const created = response?.status === 201;
      const baseLabel = savedDoc?.name || "The Brief";
      const verb = created ? "saved" : `updated to v${version}`;
      // Plain-string toast — antd 5's static message API renders ReactNode
      // content unreliably under our ConfigProvider wrapper without a
      // surrounding <App>, which is why the original PR #88 toast came up
      // invisible on staging. The "Open" link is replaced by an automatic
      // navigation below so the success path is observable + actionable
      // even if the toast itself never paints.
      message.success(`${baseLabel} ${verb} in AI Briefs.`, 6);
      // Navigate the user to the saved Document on a small delay so
      // they have time to read the toast first. The delay also gives
      // the BE a tick to settle any post-create indexing.
      if (savedDoc?.id) {
        setTimeout(() => openSavedDocument(savedDoc), 600);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Couldn't save the Brief as a Document.";
      // Log to the console too — the user can't read DevTools, but if
      // they (or we) need to forward a network trace to the BE agent,
      // this is the breadcrumb. Cheap insurance against another
      // silent-failure round-trip.
       
      console.error("[save-brief] failed:", err);
      message.error(detail, 6);
    } finally {
      setIsSaving(false);
    }
  };

  const narrative = useMemo(() => {
    const raw = extractStrategicNarrative(report);
    return raw ? normalizeNarrative(raw) : raw;
  }, [report]);
  const sortedConditions = useMemo(() => {
    const list = Array.isArray(report?.condition_analyses)
      ? report.condition_analyses
      : [];
    return [...list].sort((a, b) => {
      const aSeq =
        typeof a?.sequence_number === "number" ? a.sequence_number : 0;
      const bSeq =
        typeof b?.sequence_number === "number" ? b.sequence_number : 0;
      return aSeq - bSeq;
    });
  }, [report]);

  const [openKeys, setOpenKeys] = useState([]);

  // ---- Brief feedback (Brief-improvement series, 2026-07-23) ----
  //
  // One probe per job decides both capability and hydration: the
  // backend 403s users who aren't on the feedback rollout allowlist
  // (limited to Matthew for now), in which case the feedback bars
  // simply don't render. On 200 we index the existing rows by target
  // so each bar can PATCH its row instead of stacking duplicates.
  // Rows arrive newest-first (model ordering), so the first row seen
  // per target wins.
  const [feedbackAllowed, setFeedbackAllowed] = useState(false);
  const [feedbackByTarget, setFeedbackByTarget] = useState({});

  const feedbackKeyFor = (row) =>
    row?.condition_analysis != null
      ? `cond-${row.condition_analysis}`
      : "strategic";

  useEffect(() => {
    let cancelled = false;
    setFeedbackAllowed(false);
    setFeedbackByTarget({});
    if (jobId == null) return undefined;
    // Promise.resolve() tolerates mocked/stubbed service returns that
    // aren't thenable (CRA's resetMocks wipes module-mock
    // implementations between tests) — a falsy response just keeps
    // the feedback UI hidden.
    Promise.resolve(listBriefFeedback(jobId))
      .then((response) => {
        if (cancelled || !response) return;
        if (response.status !== 200) return; // 403 = gated off
        const rows = Array.isArray(response.data?.results)
          ? response.data.results
          : [];
        const byTarget = {};
        rows.forEach((row) => {
          const key = feedbackKeyFor(row);
          if (!(key in byTarget)) byTarget[key] = row;
        });
        setFeedbackByTarget(byTarget);
        setFeedbackAllowed(true);
      })
      .catch((err) => {
        // Feedback is an enhancement layer — a probe failure must
        // never break the report render. Log and stay hidden.
        // eslint-disable-next-line no-console
        console.error("[brief-feedback] probe failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const handleFeedbackSaved = useCallback((row) => {
    if (!row) return;
    setFeedbackByTarget((prev) => ({
      ...prev,
      [row.condition_analysis != null
        ? `cond-${row.condition_analysis}`
        : "strategic"]: row,
    }));
  }, []);

  // Auto-open the first condition once data lands so the user never
  // sees a fully-collapsed checklist on the first render. Only sets
  // once per jobId — subsequent collapse toggles come from the user.
  useEffect(() => {
    if (sortedConditions.length === 0) {
      setOpenKeys([]);
      return;
    }
    setOpenKeys((prev) => {
      if (prev.length > 0) return prev;
      const firstKey =
        sortedConditions[0].id != null
          ? String(sortedConditions[0].id)
          : "0";
      return [firstKey];
    });
    // Reset the auto-open behavior whenever a new job is loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, sortedConditions.length]);

  const handleExport = () => {
    if (!report) return;
    const md = composeReportMarkdown(report);
    if (!md) return;
    triggerMarkdownDownload(md, buildExportFilename(report));
  };

  // First-paint skeleton while the report is fetching for the first
  // time (status is "idle" until the dispatch fires, "loading" between
  // dispatch and fulfilled). We deliberately include both states so
  // the UI doesn't flash the empty state for the brief idle window.
  const showSkeleton =
    (reportState.status === "idle" || reportState.status === "loading") &&
    !report;

  const [showSlowLoadHint, setShowSlowLoadHint] = useState(false);
  useEffect(() => {
    if (!showSkeleton) {
      setShowSlowLoadHint(false);
      return undefined;
    }
    setShowSlowLoadHint(false);
    const timer = setTimeout(() => setShowSlowLoadHint(true), 8000);
    return () => clearTimeout(timer);
  }, [showSkeleton, jobId]);

  const handleReportCitationClick = useCallback(
    (parsed) => {
      if (typeof onCitationClick === "function") {
        onCitationClick(parsed, report);
        return;
      }
      _handleCitationClick(parsed, report?.document ?? null);
    },
    [onCitationClick, report]
  );

  // ---- Render branches ----

  if (jobId == null) {
    return null;
  }

  if (showSkeleton) {
    return (
      <BriefingSkeletonGroup
        data-testid="ai-report-skeleton"
        className="intentional-scroll"
        style={{
          padding: "20px 16px",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <BriefingSkeletonBlock $w="40%" $h={20} />
        <BriefingSkeletonBlock $w="100%" $h={70} />
        <BriefingSkeletonBlock $w="80%" $h={14} />
        <BriefingSkeletonBlock $w="60%" $h={14} />
        <BriefingSkeletonBlock $w="100%" $h={120} />
        {showSlowLoadHint && (
          <AnalysisErrorBanner role="status" data-testid="ai-report-slow-load">
            The Brief is still loading. If it stays here, use Retry to ask the
            server for the report again.
            <Button
              type="link"
              size="small"
              onClick={handleRetry}
              style={{ padding: "0 0 0 8px", height: "auto" }}
            >
              Retry
            </Button>
          </AnalysisErrorBanner>
        )}
      </BriefingSkeletonGroup>
    );
  }

  // First-load failure with no cached prior report — fall back to the
  // empty "Retry" view. We intentionally allow `report && status ===
  // "failed"` to fall through to the report renderer below; that
  // branch shows the cached content with a soft inline error banner so
  // the user doesn't lose their place mid-read.
  if (reportState.status === "failed" && !report) {
    return (
      <ReportEmptyBody role="alert" data-testid="ai-report-failed">
        <AnalysisErrorBanner role="alert" style={{ marginBottom: 12 }}>
          <WarningCircle
            size={14}
            weight="fill"
            style={{ marginRight: 6, verticalAlign: "middle" }}
          />
          {reportState.error || "Failed to load the analysis report."}
        </AnalysisErrorBanner>
        <Button onClick={handleRetry} data-testid="ai-report-retry">
          Retry
        </Button>
      </ReportEmptyBody>
    );
  }

  if (!report) {
    return (
      <ReportEmptyBody data-testid="ai-report-empty">
        Report not available yet.
      </ReportEmptyBody>
    );
  }

  const completed = report.completed_conditions || 0;
  const total = report.total_conditions || sortedConditions.length || 0;
  const failed = report.failed_conditions || 0;
  const elapsed = formatDuration(report.created_at, report.completed_at);
  const completedAt = formatTimestamp(report.completed_at);
  const status = report.status || "completed";

  const subtitleParts = [];
  if (completedAt) subtitleParts.push(completedAt);
  if (elapsed) subtitleParts.push(`${elapsed} elapsed`);
  if (total > 0) {
    const conditionWord = total === 1 ? "condition" : "conditions";
    if (failed > 0) {
      subtitleParts.push(
        `${completed}/${total} ${conditionWord} (${failed} failed)`
      );
    } else {
      subtitleParts.push(`${total} ${conditionWord}`);
    }
  }

  const readerAction =
    showReaderAction && !isStandalone ? (
      <Button
        icon={<ArrowSquareOut size={14} weight="bold" />}
        onClick={handleOpenReader}
        disabled={!readerPath}
        data-testid="ai-open-brief-reader"
      >
        Open in reader
      </Button>
    ) : null;

  const reportShellStyle = isStandalone
    ? {
        display: "flex",
        flexDirection: "column",
        gap: 20,
        padding: 0,
        minHeight: 0,
        overflowY: "visible",
      }
    : {
        display: "flex",
        flexDirection: "column",
        gap: 20,
        padding: "20px 16px 24px",
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
      };

  return (
    /*
     * `intentional-scroll` is load-bearing — without it the
     * app-level `useRouteScrollCleanup` hook in
     * `src/AppRouter.js` strips this div's inline `overflowY:
     * "auto"` on every pathname change (including matter-internal
     * doc switches triggered by an evidence-link click), which
     * silently freezes The Brief and resets the user's scroll
     * position. See `src/utils/scrollCleanup.js` and the matching
     * note on `AIPaneScrollBody` in `AIPaneStyled.js`.
     */
    <div
      data-testid="ai-analysis-report"
      className="intentional-scroll"
      style={reportShellStyle}
    >
      <ReportHeaderRow>
        <ReportHeaderMeta>
          <ReportHeaderTitle>Attorney Analysis Report</ReportHeaderTitle>
          {subtitleParts.length > 0 && (
            <ReportHeaderSubtitle data-testid="ai-report-subtitle">
              {subtitleParts.join(" · ")}
            </ReportHeaderSubtitle>
          )}
        </ReportHeaderMeta>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {readerAction}
          <AnalysisStatusPill $status={status} data-testid="ai-report-status-pill">
            <AnalysisStatusPillDot />
            {STATUS_LABEL[status] || status}
          </AnalysisStatusPill>
        </div>
      </ReportHeaderRow>

      {/* Soft banner if the most recent fetch failed but we have
          cached content — keep the user reading rather than blocking
          them on a hard error wall. */}
      {reportState.status === "failed" && (
        <AnalysisErrorBanner role="alert" data-testid="ai-report-stale-banner">
          <WarningCircle
            size={14}
            weight="fill"
            style={{ marginRight: 6, verticalAlign: "middle" }}
          />
          {reportState.error || "Couldn't refresh the report."}{" "}
          <Button
            type="link"
            size="small"
            onClick={handleRetry}
            style={{ padding: 0, height: "auto", verticalAlign: "baseline" }}
          >
            Retry
          </Button>
        </AnalysisErrorBanner>
      )}

      {/* Deterministic combined rating — computed server-side from the
          graph's codesheet determinations via the 38 CFR 4.25/4.26
          calculator (never by the LLM). The rounded value leads; the
          exact table math is the small muted line beneath it. */}
      {report.combined_rating?.combined_pct != null && (
        <CombinedRatingCard data-testid="ai-combined-rating">
          <h3>Combined Rating (codesheet)</h3>
          <CombinedRatingValue>
            {report.combined_rating.combined_pct}%
          </CombinedRatingValue>
          {report.combined_rating.note && (
            <CombinedRatingNote data-testid="ai-combined-rating-note">
              {report.combined_rating.note}
            </CombinedRatingNote>
          )}
        </CombinedRatingCard>
      )}

      {narrative && (
        <StrategicOverviewCard data-testid="ai-strategic-overview">
          <h3>
            <Sparkle
              size={12}
              weight="fill"
              style={{ marginRight: 6, verticalAlign: "middle" }}
            />
            Strategic Overview
          </h3>
          <StrategicOverviewBody>
            <BriefingMarkdown
              content={narrative}
              onCitationClick={handleReportCitationClick}
            />
          </StrategicOverviewBody>
          {feedbackAllowed && (
            <BriefFeedbackBar
              jobId={jobId}
              targetKind="strategic_overview"
              existing={feedbackByTarget.strategic || null}
              onSaved={handleFeedbackSaved}
            />
          )}
        </StrategicOverviewCard>
      )}

      {sortedConditions.length === 0 ? (
        <AnalysisHelperText>
          The analysis didn&apos;t return any per-condition results. The
          backend may have run on an unsupported document type.
        </AnalysisHelperText>
      ) : (
        <ConditionAnalysisCollapse data-testid="ai-condition-collapse">
          <Collapse
            activeKey={openKeys}
            onChange={(keys) =>
              setOpenKeys(Array.isArray(keys) ? keys : [keys])
            }
            expandIconPosition="end"
            ghost={false}
            items={sortedConditions.map((cond) => {
              const key =
                cond.id != null ? String(cond.id) : `seq-${cond.sequence_number}`;
              const chips = buildConditionChips(cond);
              const condFailed = cond.status === "failed";
              return {
                key,
                "data-testid": "ai-condition-panel",
                "data-condition-status": cond.status || "completed",
                label: (
                  <ConditionPanelHeader>
                    <ConditionStatusIcon status={cond.status} />
                    <ConditionPanelTitle title={cond.condition_name}>
                      {cond.condition_name || "Unnamed condition"}
                    </ConditionPanelTitle>
                    {chips.length > 0 && (
                      <ConditionMetaChipRow data-testid="ai-condition-chips">
                        {chips.map((chip) => (
                          <ConditionMetaChip key={chip.key} $tone={chip.tone}>
                            {chip.label}
                          </ConditionMetaChip>
                        ))}
                      </ConditionMetaChipRow>
                    )}
                  </ConditionPanelHeader>
                ),
                children: (
                  <ConditionAnalysisBody>
                    {condFailed && (
                      <ConditionFailedBanner>
                        <XCircle size={14} weight="fill" />
                        <span>
                          <strong>This condition failed to analyze.</strong>{" "}
                          {cond.error_message || "No error message reported."}
                        </span>
                      </ConditionFailedBanner>
                    )}
                    {cond.analysis_text ? (
                      <BriefingMarkdown
                        content={cond.analysis_text}
                        onCitationClick={handleReportCitationClick}
                      />
                    ) : (
                      <AnalysisHelperText>
                        No analysis text was produced for this condition.
                      </AnalysisHelperText>
                    )}
                    {feedbackAllowed && cond.id != null && (
                      <BriefFeedbackBar
                        jobId={jobId}
                        targetKind="condition_analysis"
                        conditionAnalysisId={cond.id}
                        existing={feedbackByTarget[`cond-${cond.id}`] || null}
                        onSaved={handleFeedbackSaved}
                      />
                    )}
                  </ConditionAnalysisBody>
                ),
              };
            })}
          />
        </ConditionAnalysisCollapse>
      )}

      {feedbackAllowed && jobId != null && (
        <BriefChatPanel jobId={jobId} onBriefUpdated={handleRetry} />
      )}

      <ReportFooter>
        <Button
          icon={<FileArrowDown size={14} weight="bold" />}
          type="primary"
          onClick={handleSaveAsDocument}
          loading={isSaving}
          disabled={saveMatterId == null || jobId == null || isSaving}
          data-testid="ai-save-as-document"
        >
          Save as Document
        </Button>
        <Button
          icon={<DownloadSimple size={14} weight="bold" />}
          onClick={handleExport}
          data-testid="ai-export-report"
        >
          Export as Markdown
        </Button>
        <Button
          icon={<Sparkle size={14} weight="fill" />}
          onClick={handleRerun}
          loading={isReRunning}
          disabled={Boolean(rerunDisabledReason) || isReRunning}
          title={rerunDisabledReason || "Run this Brief again"}
          data-testid="ai-rerun-report"
        >
          Run again
        </Button>
        {/* Hidden management hook used by AnalysisTab when the user
            kicks off a fresh re-run on the same doc — gives us a
            one-line "drop the report" path without needing the slice
            consumer to know the jobId twice. Kept after the visible
            "Run again" wiring landed because some tests reach for
            this stable handle, and there's no harm in leaving it. */}
        <span style={{ display: "none" }}>
          <button
            type="button"
            data-testid="ai-clear-report"
            onClick={handleClearReport}
          >
            clear
          </button>
        </span>
      </ReportFooter>
      {rerunDisabledReason && (
        <AnalysisHelperText role="status" data-testid="ai-rerun-help">
          {rerunDisabledReason}
        </AnalysisHelperText>
      )}
    </div>
  );
};

AnalysisReport.propTypes = {
  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.oneOf(["panel", "standalone"]),
  onCitationClick: PropTypes.func,
  showReaderAction: PropTypes.bool,
  fallbackMatterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

AnalysisReport.defaultProps = {
  jobId: null,
  variant: "panel",
  onCitationClick: undefined,
  showReaderAction: true,
  fallbackMatterId: null,
};

export default AnalysisReport;
